import React, { type ReactElement, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import ConfirmModal, { ConfirmModalActionPair } from 'atom-components/confirm-modal';
import { pluralise } from 'helpers/string.helpers';
import type {
  MarkItemFormValue,
  MarkResponseFormValues
} from 'pages/marking/subpages/mark-response-page/mark-response-form/mark-response-form.schema';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import { usePostTestInstanceResponseMarker } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { PostTestInstanceResponseMarker } from 'services/assist-api/entities/test-instance-response-marker/types';

/**
 * Returns the mark items that will be added or have been changed by the current user.
 */
export function newAndChangedMarkItems(
  savedMarkItems: MarkItemFormValue[],
  markItems: MarkItemFormValue[]
): MarkItemFormValue[] {
  const changedMarkItems = markItems
    .filter((mi) => mi.id !== undefined)
    .filter((mi) =>
      savedMarkItems.find((smi) => smi.id === mi.id && smi.submittedValue !== mi.submittedValue)
    );
  const newMarkItems = markItems.filter((mi) => mi.id === undefined && mi.submittedValue !== '');
  return changedMarkItems.concat(newMarkItems);
}

/**
 * If the user (admin) has not been set as the active marker and they have changed some mark items,
 * prevent saving until they have been set as the active marker.
 *
 * Also prevent saving if there is no active marker and the current user can administrate the mark -
 * this provides the marking admin the opportunity to set themselves as the active marker of a new
 * mark or of a saved mark where there is somehow no active marker set.
 *
 * Non-admin markers should never get this far into the form, but it is safer to also check that the
 * current user can administrate the mark in case an earlier check slipped through the cracks;
 * non-admin markers should never be able to reassign marks.
 */
export function shouldAskToChangeActiveMarker(
  canAdministrateMark: boolean,
  defaultValues: MarkResponseFormValues,
  formValues: MarkResponseFormValues
): boolean {
  const newChangedMarkItems = newAndChangedMarkItems(defaultValues.markItems, formValues.markItems);
  return (
    canAdministrateMark &&
    ((formValues.currentUserMarkerId !== formValues.activeMarkerId &&
      newChangedMarkItems.length > 0) ||
      !formValues.activeMarkerId)
  );
}

const currentUserMarkerIdFieldName = 'currentUserMarkerId';
const activeMarkerIdFieldName = 'activeMarkerId';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  responseId: number;
  currentOrganisationUser: MeCustomerUser;
};

export default function ConfirmMarkerChangeModal({
  isOpen,
  onClose,
  onConfirm,
  responseId,
  currentOrganisationUser
}: Props): ReactElement {
  const { formState, setValue } = useFormContext();
  const { watch } = useFormContext();
  const activeMarkerId = watch('activeMarkerId');
  const markItems = watch('markItems');
  const currentUserMarkerId = watch(currentUserMarkerIdFieldName);
  const newChangedMarkItems = newAndChangedMarkItems(formState.defaultValues?.markItems, markItems);

  const postTestInstanceResponseMarker = usePostTestInstanceResponseMarker({
    onSuccess: ({ data }) => {
      toast.success(`Allocated current marker to response #${data.json.testInstanceResponse.id}.`);
    },
    onError: (error) => {
      toast.error(`Failed to allocate current marker to response: ${error.message}`);
    }
  });

  const createTestInstanceResponseMarker = useCallback(
    async (testInstanceResponseId: number, organisationUserId: number) => {
      const markerToPost: PostTestInstanceResponseMarker = {
        testInstanceResponseId,
        customerUserId: organisationUserId
      };
      postTestInstanceResponseMarker.mutate(markerToPost, {
        onSuccess: (data) => {
          const newlyCreatedMarkerId = data.data.json.id;
          // After creating a response marker for the current user, set the newly created marker as
          // the active marker:
          setValue(currentUserMarkerIdFieldName, newlyCreatedMarkerId, {
            shouldValidate: true,
            shouldTouch: true
          });
          setValue(activeMarkerIdFieldName, newlyCreatedMarkerId, {
            shouldValidate: true,
            shouldTouch: true
          });
          onConfirm();
        }
      });
    },
    [onConfirm, postTestInstanceResponseMarker, setValue]
  );

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Confirm Marker Change"
      handleClickCancel={onClose}
      handleClickOK={(): void => {
        if (currentUserMarkerId === undefined) {
          createTestInstanceResponseMarker(responseId, currentOrganisationUser.id);
        } else {
          // If there is already a response marker, set the active marker to the current user marker:
          setValue(activeMarkerIdFieldName, currentUserMarkerId, {
            shouldValidate: true,
            shouldTouch: true
          });
          onConfirm();
        }
      }}
      actionPair={ConfirmModalActionPair.Custom}
      buttonTitleOK="Change Active Marker"
      buttonTitleCancel="Cancel"
    >
      <p>
        {activeMarkerId
          ? `You have made changes to ${newChangedMarkItems.length} ${pluralise('item', newChangedMarkItems.length)}.`
          : `There is currently no active marker.`}{' '}
        To save these changes you must be assigned as the active marker.
      </p>
    </ConfirmModal>
  );
}
